import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from './apiClient';

/* ============================================================================
 * useAliases — shared, backend-backed display aliases for:
 *   • warehouse  (DPW → "Main Warehouse")
 *   • area       (11-01 → "Cold Room A")
 *   • palletType (95 → "Big Pallet")
 *
 * The hook fetches once on mount, caches in state, and exposes a save()
 * helper that PUTs the full map and refetches. Every component that imports
 * this hook shares the same React state via a module-level event bus so a
 * save in the FilterBar instantly propagates to charts, drills, etc.
 * ========================================================================== */

export type AliasCategory = 'warehouse' | 'area' | 'palletType' | 'customer';
export type AliasMap = Record<string, string>;            // code → displayName
export type AllAliases = Record<AliasCategory, AliasMap>; // { warehouse: {...}, area: {...}, palletType: {...}, customer: {...} }

const EMPTY: AllAliases = { warehouse: {}, area: {}, palletType: {}, customer: {} };

// Module-level event bus so all hook instances stay in sync
const listeners = new Set<(a: AllAliases) => void>();
let cached: AllAliases = { ...EMPTY };

function notify(a: AllAliases) {
  cached = a;
  listeners.forEach(fn => fn(a));
}

async function fetchAliases(): Promise<AllAliases> {
  try {
    const res = await api.get('/display-aliases');
    const d = res.data?.data;
    return {
      warehouse:  d?.warehouse  ?? {},
      area:       d?.area       ?? {},
      palletType: d?.palletType ?? {},
      customer:   d?.customer   ?? {},
    };
  } catch (e) {
    console.warn('useAliases: fetch failed, using empty aliases', e);
    return { ...EMPTY };
  }
}

async function saveAliases(aliases: AllAliases): Promise<boolean> {
  try {
    await api.put('/display-aliases', { aliases });
    const fresh = await fetchAliases();
    notify(fresh);
    return true;
  } catch (e) {
    console.error('useAliases: save failed', e);
    return false;
  }
}

let initialFetchDone = false;

export function useAliases() {
  const [aliases, setAliases] = useState<AllAliases>(cached);

  useEffect(() => {
    // Subscribe to cross-instance updates
    listeners.add(setAliases);
    // Initial fetch (once per app lifecycle)
    if (!initialFetchDone) {
      initialFetchDone = true;
      fetchAliases().then(a => notify(a));
    }
    return () => { listeners.delete(setAliases); };
  }, []);

  const save = useCallback(async (next: AllAliases) => {
    return saveAliases(next);
  }, []);

  const refresh = useCallback(async () => {
    const a = await fetchAliases();
    notify(a);
  }, []);

  /** Resolve a code to its display name (or the code itself if no alias). */
  const resolve = useCallback((category: AliasCategory, code: string): string => {
    const name = aliases[category]?.[code];
    return name ? `${name}` : code;
  }, [aliases]);

  /** Resolve with code in parentheses: "Big Pallet (95)" */
  const resolveWithCode = useCallback((category: AliasCategory, code: string): string => {
    const name = aliases[category]?.[code];
    return name ? `${name} (${code})` : code;
  }, [aliases]);

  return useMemo(() => ({
    aliases,
    save,
    refresh,
    resolve,
    resolveWithCode,
  }), [aliases, save, refresh, resolve, resolveWithCode]);
}
